import type { PivotDims } from "@/types";

// Single registry of the pivot dimensions: the API's `group_by` / `column_by`
// values, the filter key used by `filters` and `filter_options`, and how a
// row's dims render. Everything that lists dimensions reads from here.
export interface PivotDimension {
  value: string;
  label: string;
  filterKey: string;
  get: (dims: PivotDims) => string | null;
  // Rendered as a thumbnail (see `dimensionImage`) rather than as text.
  render?: "image";
  // Ordered calendar buckets: as columns they can be charted as a line.
  temporal?: true;
}

export interface DimensionImage {
  url: string;
  mediaType: "image" | "video";
}

const text = (key: string) => (dims: PivotDims) => (dims[key] == null ? null : String(dims[key]));

export const PIVOT_DIMENSIONS: PivotDimension[] = [
  { value: "account", label: "Account", filterKey: "account_id", get: text("account_name") },
  { value: "survey", label: "Survey", filterKey: "survey_id", get: text("survey_name") },
  { value: "year", label: "Year", filterKey: "year", get: text("year"), temporal: true },
  { value: "quarter", label: "Quarter", filterKey: "quarter", get: text("quarter"), temporal: true },
  { value: "month", label: "Month", filterKey: "month", get: text("month"), temporal: true },
  { value: "day", label: "Day", filterKey: "day", get: text("day"), temporal: true },
  { value: "m_campaign", label: "MCampaign", filterKey: "m_campaign_id", get: text("m_campaign_name") },
  { value: "m_status", label: "MCampaign status", filterKey: "m_status", get: text("m_campaign_status") },
  {
    value: "c_campaign",
    label: "CCampaign",
    filterKey: "c_campaign_id",
    get: (dims) => {
      const name = text("c_campaign_name")(dims);
      return name?.split(" - ").slice(1).join(" - ") || name;
    },
  },
  { value: "country", label: "Country", filterKey: "country", get: text("country") },
  { value: "audience", label: "Audience", filterKey: "audience_id", get: text("audience_name") },
  { value: "adset", label: "Adset", filterKey: "adset_id", get: text("adset_name") },
  { value: "gender", label: "Adset gender", filterKey: "gender", get: text("gender") },
  {
    value: "age",
    label: "Adset age",
    filterKey: "age",
    get: (dims) => (dims.age_min != null ? `${dims.age_min}–${dims.age_max}` : null),
  },
  { value: "m_creative", label: "Creative", filterKey: "m_creative_id", get: text("m_creative_name") },
  { value: "template", label: "Template", filterKey: "template_id", get: text("template_name") },
  { value: "image", label: "Image", filterKey: "image_id", get: text("image_name"), render: "image" },
];

export const DEFAULT_ROW_DIMENSIONS = ["c_campaign"];

const byValue = new Map(PIVOT_DIMENSIONS.map((d) => [d.value, d]));

export const findDimension = (value: string): PivotDimension | undefined => byValue.get(value);

export const dimensionLabel = (value: string): string => byValue.get(value)?.label ?? value;

export const isTemporalDimension = (value: string): boolean => byValue.get(value)?.temporal === true;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Compact axis label for a temporal bucket: "8 Jul", "Jul 2026", "Q3 2026", "2026".
export const shortTemporalLabel = (value: string, raw: string): string => {
  if (value === "day") {
    const [, m, d] = raw.split("-");
    return m && d ? `${Number(d)} ${MONTHS[Number(m) - 1]}` : raw;
  }
  if (value === "month") {
    const [y, m] = raw.split("-");
    return m ? `${MONTHS[Number(m) - 1]} ${y}` : raw;
  }
  if (value === "quarter") {
    const [y, q] = raw.split("-");
    return q ? `${q} ${y}` : raw;
  }
  return raw;
};

// Label of one dimension inside a dims hash; "?" marks the bucket the API could
// not attribute (e.g. a cost line whose ad resolves to no creative).
export const dimensionValue = (value: string, dims: PivotDims): string => byValue.get(value)?.get(dims) ?? "?";

// The visual behind an `image` bucket, when the API resolved one.
export const dimensionImage = (value: string, dims: PivotDims): DimensionImage | null => {
  if (byValue.get(value)?.render !== "image" || !dims.image_url) return null;
  return { url: String(dims.image_url), mediaType: dims.image_media_type === "video" ? "video" : "image" };
};

// An ordered, checkable list of every dimension — the shape both pickers bind to.
export interface DimensionPick {
  value: string;
  checked: boolean;
}

export const defaultDimensionPicks = (checked: string[] = []): DimensionPick[] =>
  PIVOT_DIMENSIONS.map((d) => ({ value: d.value, checked: checked.includes(d.value) }));

export const checkedValues = (picks: DimensionPick[]): string[] => picks.filter((p) => p.checked).map((p) => p.value);

// The checked dimensions first, in the given (nesting) order, then the rest in registry order.
export const picksFromChecked = (values: string[]): DimensionPick[] => {
  const checked = values.filter((v) => byValue.has(v));
  const seen = new Set(checked);
  return [
    ...checked.map((value) => ({ value, checked: true })),
    ...PIVOT_DIMENSIONS.filter((d) => !seen.has(d.value)).map((d) => ({ value: d.value, checked: false })),
  ];
};
